import { Injectable, autobind } from '@kaokei/use-vue-service';

import { DaoService } from '../dao.service';

import { checkBrowser, checkDomainConnection, preloadIDE } from '@/utils/check';

import { MediaService } from '@/services/exam/media.service';
import { CountdownService } from '@/services/countdown.service';
import { CosService } from '@/services/cos.service';
import { UserService } from '@/services/user.service';
import { CheatService } from './cheat.service';

import Sentry from '@/utils/sentry';

const COUNTDOWN_TIME = 3 * 60; // 环境检测倒计时的总时间
const RECORD_DURATION = 15; // 视频监控-记录音视频15秒
const RECORD_DURATION_GAP = 2 * 60 * 1000; // 视频监控-间隔2分钟

@Injectable()
export class EnvCheckService {
  /**
   * 手动注入CheatService
   * 因为在环境检测页面是不依赖这个服务的
   *
   * @type {CheatService}
   * @memberof EnvCheckService
   */
  public cheatService!: CheatService;

  /**
   * 环境检测-检测阶段
   * 1未开始 2检测中 3检测结束
   *
   * @memberof EnvCheckService
   */
  public stage = 1;

  /**
   * 环境监测-服务器端生成的检测ID
   *
   * @memberof EnvCheckService
   */
  public checkId = 0;

  /**
   * 环境监测-检测超时
   *
   * @memberof EnvCheckService
   */
  public timeout = false;

  /**
   * 环境监测-正常检测完成-包括成功和失败
   *
   * @memberof EnvCheckService
   */
  public finished = false;

  /**
   * 考试时视频监控-定时录制视频的定时器
   *
   * @memberof EnvCheckService
   */
  public timer = 0;

  /**
   * 考试时视频监控-标志视频监控已经结束
   * 比如组件卸载了
   * 比如考试结束了-手动交卷和自动交卷
   *
   * @memberof EnvCheckService
   */
  public destroyed = false;

  /**
   * 检测浏览器的结果
   *
   * @type {*}
   * @memberof EnvCheckService
   */
  public checkBrowserResult: any;
  /**
   * 检测域名连通性的结果
   *
   * @type {*}
   * @memberof EnvCheckService
   */
  public checkDomainConnectionResult = [] as any;
  /**
   * 预加载ide的结果
   * 1代表成功 0代表失败
   *
   * @memberof EnvCheckService
   */
  public preloadIDEResult = 0;
  /**
   * 检测视频和音频的结果
   * 如果正常，则结果是对应的oss URL
   * 如果不正常，则是对应的异常
   *
   * @type {*}
   * @memberof EnvCheckService
   */
  public videoAndAudioResult: any;

  /**
   * 视频监控-需要记录考试ID
   *
   * @type {number}
   * @memberof EnvCheckService
   */
  public answerPaperId!: number;

  constructor(
    public daoService: DaoService,
    public mediaService: MediaService,
    public countdownService: CountdownService,
    public cosService: CosService,
    public userService: UserService
  ) {}

  public setCheatService(service: CheatService) {
    this.cheatService = service;
  }

  /**
   * 检测浏览器不符合要求
   *
   * @readonly
   * @memberof EnvCheckService
   */
  public get isBrowserNotValid() {
    const result = this.checkBrowserResult;
    if (result) {
      // 检测结果已经存在并且不是最新版本谷歌浏览器
      return !(result.isChrome && result.chromeMajorVersion >= 80);
    }
  }

  /**
   * 获取视频数据是否报错且是未授权错误
   *
   * @readonly
   * @memberof EnvCheckService
   */
  public get isVideoNotAllowed() {
    const result = this.videoAndAudioResult;
    if (result && result.name) {
      // 检测结果已经存在并且没有授权使用摄像头和麦克风
      return result.name === 'NotAllowedError';
    }
  }

  /**
   * 获取视频数据是否报错
   *
   * @readonly
   * @memberof EnvCheckService
   */
  public get isVideoError() {
    const result = this.videoAndAudioResult;
    if (result) {
      return (
        // 如果没有videoUrl则代表报错了
        // 但是需要排除掉网络错误和上传错误
        !result.videoUrl && !result.errCode && !this.isNetworkError(result)
      );
    }
  }

  /**
   * safari不支持MediaRecorder
   *
   * @readonly
   * @memberof EnvCheckService
   */
  public get isNotSupportMediaRecorder() {
    const result = this.videoAndAudioResult;
    if (result) {
      return (
        // 如果没有videoUrl则代表报错了
        // 但是需要排除掉网络错误和上传错误
        result.name === 'ReferenceError' &&
        result.message?.indexOf('MediaRecorder') >= 0
      );
    }
  }

  public isNetworkError(err: any) {
    return err?.name === 'Error' && err?.message === 'Network Error';
  }

  /**
   * 环境检测可能会在两个地方使用
   * 在检测页面使用
   * 在考试页面使用，在考试页面需要支持视频监控
   * 而视频监控需要answerPaperId这个参数
   *
   * @param {number} id
   * @memberof EnvCheckService
   */
  public setAnswerPaperId(id: number) {
    this.answerPaperId = id;
  }

  /**
   * 重置所有实例属性状态
   *
   * @memberof EnvCheckService
   */
  @autobind
  public reset() {
    this.stage = 1;
    this.checkId = 0;
    this.timeout = false;
    this.finished = false;

    this.timer = 0;
    this.destroyed = false;
    this.checkBrowserResult = undefined;
    this.checkDomainConnectionResult = [];
    this.preloadIDEResult = 0;
    this.videoAndAudioResult = undefined;
    this.answerPaperId = 0;
  }

  /**
   * 主要是释放定时器以及音视频流
   *
   * @memberof EnvCheckService
   */
  public dispose() {
    this.destroyed = true;
    window.clearTimeout(this.timer);
    this.mediaService.dispose();
  }

  /**
   * 获取检测浏览器的结果
   * 这个方法是有副作用的 - 会把检测结果记录到实例属性上
   *
   * @return {*}
   * @memberof EnvCheckService
   */
  public getCheckBrowserResult() {
    const result = checkBrowser();
    this.checkBrowserResult = result;
    return result;
  }

  /**
   * 获取检测域名连通性的结果
   * 这个方法是有副作用的 - 会把检测结果记录到实例属性上
   *
   * @return {*}
   * @memberof EnvCheckService
   */
  public getCheckDomainConnectionResult() {
    return checkDomainConnection().then(res => {
      this.checkDomainConnectionResult = res;
      return res;
    });
  }

  /**
   * ide预加载的结果
   * 这个方法是有副作用的 - 会把检测结果记录到实例属性上
   * 1代表预加载成功 0代表预加载失败
   *
   * @return {*}
   * @memberof EnvCheckService
   */
  public getPreloadIDEResult() {
    return preloadIDE().then(() => {
      this.preloadIDEResult = 1;
      return 1;
    });
  }

  /**
   * 获取音视频的结果
   * 这个方法是有副作用的 - 会把检测结果记录到实例属性上
   *
   * @return {*}
   * @memberof EnvCheckService
   */
  public getVideoAndAudio() {
    this.cheatService && this.cheatService.disable();

    return this.mediaService
      .getMediaStream()
      .then(() => {
        this.cheatService && this.cheatService.enable();
        const recordRet = this.mediaService.recordMedia(RECORD_DURATION);

        return recordRet.result
          .then(res => {
            const fileName = this.cosService.getBlobName(
              this.userService.user?.userId,
              res.name
            );
            return this.cosService.uploadBlob(res.data, fileName);
          })
          .then(res => {
            this.videoAndAudioResult = { videoUrl: res.Location };
            return this.videoAndAudioResult;
          });
      })
      .catch(e => {
        this.cheatService && this.cheatService.enable();
        this.videoAndAudioResult = e;

        Sentry.withScope(function (scope) {
          scope.setTag('api', 'getVideoAndAudio');
          scope.setLevel(Sentry.Severity.Debug);
          Sentry.captureException(e);
        });

        return e;
      });
  }

  /**
   * 正常环境检测结束的后续处理方法
   * 对应的是检测超时处理方法
   *
   * @param {*} result
   * @return {*}
   * @memberof EnvCheckService
   */
  public normalFinishCheck() {
    if (!this.checkId) return; // 如果checkId不存在，则后续不处理了
    if (this.timeout) return; // 如果已经超时了，则后续不处理了
    this.finished = true;
    this.uploadCheckRecord();
    this.mediaService.dispose();
    this.stage = 3;
  }

  /**
   * 环境检测超时的处理方法
   *
   * @return {*}
   * @memberof EnvCheckService
   */
  @autobind
  public timeoutCallback() {
    if (!this.checkId) return; // 如果checkId不存在，则后续不处理了
    if (this.finished) return; // 如果已经正常检测完成了，则后续不处理了
    this.timeout = true;
    this.uploadCheckRecord();
    this.mediaService.dispose();
    this.stage = 3;
  }

  /**
   * 开始环境检测
   *
   * @memberof EnvCheckService
   */
  public async startCheck() {
    await this.getStartCheckId();

    Promise.all([
      this.getCheckBrowserResult(), // 一定成功
      this.getCheckDomainConnectionResult(), // 一定成功，可能超时
      this.getVideoAndAudio(), // 一定成功，catch了所有异常
      this.getPreloadIDEResult(), // 一定成功，可能超时，iframe load事件标志成功
      this.delay10s(), // 一定成功，10+秒后完成
    ]).then(() => {
      // 取消超时的回调函数
      this.countdownService.unregisterCallback();
      // 检测结束
      this.normalFinishCheck();
    });

    // 开始显示倒计时
    this.countdownService.setUnit(COUNTDOWN_TIME);
    // 注册超时的回调函数
    this.countdownService.registerCallback(this.timeoutCallback);
    // 开始倒计时
    this.countdownService.start();
  }

  /**
   * 开始环境检测，从服务器端获取检测ID
   *
   * @return {*}
   * @memberof EnvCheckService
   */
  public getStartCheckId() {
    return this.daoService.addCheckRecord().then(res => {
      this.checkId = res;
    });
  }

  /**
   * 检测完成，需要保存检测结果
   *
   * @return {*}
   * @memberof EnvCheckService
   */
  public uploadCheckRecord() {
    const result = {
      id: this.checkId,
      result:
        this.checkBrowserResult &&
        this.checkBrowserResult.isChrome &&
        this.checkBrowserResult.chromeMajorVersion >= 80 &&
        this.checkDomainConnectionResult &&
        this.checkDomainConnectionResult.length === 0 &&
        this.videoAndAudioResult &&
        this.videoAndAudioResult.videoUrl
          ? 1
          : 0,
      checkItems: {
        userAgent: navigator.userAgent,
        timeout: this.timeout,
        domainConnection: this.checkDomainConnectionResult || [],
        preloadIDE: this.preloadIDEResult,
        checkVideoAndAudioState:
          this.videoAndAudioResult && this.videoAndAudioResult.videoUrl ? 1 : 0, //1代表检测成功，0代表失败
        checkVideoAndAudioResult: this.videoAndAudioResult || {},
        ...(this.checkBrowserResult || {}),
      },
    };
    return this.daoService.updateCheckRecord(result);
  }

  /**
   * 延迟10秒钟
   *
   * @return {*}
   * @memberof EnvCheckService
   */
  public delay10s() {
    // 每次检测至少10s以上，否则显得不够真实
    return new Promise(resolve => {
      const ran = Math.floor(Math.random() * 10) + 10;
      window.setTimeout(() => resolve(true), ran * 1000);
    });
  }

  /**
   * 开始视频监控
   * 本质就是重复的获取视频流，重复录制音视频
   *
   * @memberof EnvCheckService
   */
  public startVideoMonitor() {
    // 视频监控也需要检测浏览器版本
    if (this.destroyed) return;
    this.getCheckBrowserResult();
    this.getVideoAndAudio()
      .then(res => {
        if (res && res.videoUrl) {
          return this.monitorRecord(res.videoUrl);
        }
      })
      .then(() => {
        if (this.destroyed) return;
        this.timer = window.setTimeout(
          () => this.startVideoMonitor(),
          RECORD_DURATION_GAP
        );
      })
      .catch(e => {
        if (this.destroyed) return;
        this.timer = window.setTimeout(
          () => this.startVideoMonitor(),
          RECORD_DURATION_GAP
        );

        Sentry.withScope(function (scope) {
          scope.setTag('api', 'startVideoMonitor');
          scope.setLevel(Sentry.Severity.Debug);
          Sentry.captureException(e);
        });
      });
  }

  /**
   * 上传视频监控记录
   *
   * @param {string} url
   * @return {*}
   * @memberof EnvCheckService
   */
  public monitorRecord(url: string) {
    return this.daoService.monitorRecord({
      url,
      answerPaperId: this.answerPaperId,
    });
  }
}
